import React, { CSSProperties, ForwardedRef, MutableRefObject, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css, CSSObject } from 'styled-components'
import { colors, colorSystem, fontSystem, zIndexSystem } from '@/styles/theme'
import { isEmpty, isNil, keyBy } from 'lodash-es'
import { Icon, HStack, VStack } from '@musinsa/mwms-ds'
import { Icon as IconBase } from '@musinsa/pbo-assets'
import Text from '@/components/content/Text'
import useClickOutside from '@/hooks/useClickOutside'
import { createPortal } from 'react-dom'
import FieldLabel from '@/components/content/FieldLabel'
import { Size, SizeProps } from './SelectBoxTypes'

export type SelectOptionType<T = string | number | boolean> = {
  key?: string
  value: T
  label: string
  disabledCheckValue?: string | number | boolean
}

export interface BaseSelectBoxProps<T = string | number | boolean> extends SizeProps {
  portalId?: string | false
  maxHeight?: number
  className?: string
  minWidth?: number
  options: SelectOptionType<T>[]
  readonly?: boolean
  placeholder?: string
  disabled?: boolean
  position?: 'top' | 'bottom'
  parentRef?: RefObject<HTMLDivElement>
}

export interface SelectBoxProps<T = string | number | boolean> extends BaseSelectBoxProps<T> {
  block?: boolean
  hasAll?: boolean
  label?: React.ReactNode
  required?: boolean
  value?: T
  onChange?: (v: T) => void
  onClose?: () => void
  onClear?: () => void
  disabledValues?: Array<string | number | boolean>
  kind?: 'normal' | 'highlight'
  style?: CSSProperties
  // All
  allValue?: string | number | boolean | null
  allLabel?: string
  // Empty
  emptyOpt?: string
  optionDomDataTestId?: string
}

export type SelectBoxWithoutOptionProps<T> = Omit<SelectBoxProps<T>, 'options'>

const SIZE: Record<Size, number> = {
  sm: 26,
  md: 28,
  lg: 36,
  xl: 0, // 사용안함
} as const

function SelectBoxInner<T = string | number | boolean>(props: SelectBoxProps<T>, selectRef: ForwardedRef<HTMLDivElement>) {
  const {
    className,
    optionDomDataTestId,
    minWidth,
    portalId = 'root',
    label,
    block,
    hasAll = false,
    required = false,
    options,
    value,
    style,
    disabled = false,
    readonly = false,
    kind = 'normal',
    placeholder = '옵션을 선택해 주세요.',
    emptyOpt = '옵션이 없습니다.',
    allValue = null,
    allLabel,
    onChange,
    onClose,
    onClear,
    disabledValues,
    size = 'md',
    position = 'bottom',
    maxHeight,
    parentRef,
  } = props

  // set ref
  const $ref = useRef<HTMLDivElement>(null)
  const $select = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if ($select.current && selectRef) {
      ;(selectRef as MutableRefObject<HTMLDivElement>).current = $select.current
    }
  }, [$select])

  const optionList = useMemo<SelectOptionType<T>[]>(
    () => (hasAll ? [{ label: allLabel || '전체', value: allValue as T }, ...options] : options),
    [options, allValue, allLabel, hasAll],
  )
  const optionMap = useMemo<Record<string, SelectOptionType<T>>>(() => keyBy(optionList, (opt) => `${opt.value}`), [optionList])
  const [open, setOpen] = useState(false)
  const [openedStyle, setOpenedStyle] = useState<CSSObject>({})
  const handleOpen = useCallback(() => {
    if (disabled || readonly) return

    setOpen(true)
  }, [selectRef, size, disabled])

  const handleClose = useCallback(() => {
    setOpen(false)
    if (onClose) onClose()
  }, [onClose])

  useClickOutside($ref, handleClose)

  const onClickOption = useCallback(
    (v: SelectOptionType<T>['value']) => {
      if (onChange) onChange(v)
      setTimeout(handleClose, 0)
    },
    [onChange, handleClose],
  )

  const onSetOpenedStyle = useCallback(() => {
    if (!portalId || !$select.current) return

    const { top, left, width, bottom } = $select.current.getBoundingClientRect()
    if (position === 'top') {
      setOpenedStyle({
        position: 'fixed',
        bottom: `${bottom + SIZE[size]}px`,
        left: `${left}px`,
        width: `${width}px`,
        right: 'unset',
      })
    } else {
      setOpenedStyle({
        position: 'fixed',
        top: `${top + SIZE[size]}px`,
        left: `${left}px`,
        width: `${width}px`,
        right: 'unset',
      })
    }
  }, [optionList, portalId])

  useEffect(() => {
    if (portalId) {
      setTimeout(() => {
        onSetOpenedStyle()
      }, 0)
    }
  }, [portalId, onSetOpenedStyle])

  useEffect(() => {
    const onResize = () => {
      handleClose()
      onSetOpenedStyle()
    }

    window.addEventListener('resize', onResize)
    if (parentRef?.current) parentRef.current.addEventListener('scroll', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      if (parentRef?.current) parentRef.current.removeEventListener('scroll', onResize)
    }
  }, [handleClose, onSetOpenedStyle])

  const optionDom = (
    <SelectBoxOptions
      $size={size}
      style={openedStyle}
      gap={2}
      p={8}
      $label={!!label}
      $block={block}
      align="flex-start"
      $position={position}
      $maxHeight={maxHeight}
      dataTestId={optionDomDataTestId}
    >
      {!options.length ? (
        <Option>{emptyOpt}</Option>
      ) : (
        optionList.map((option, idx) => {
          const isDisabled = (option.disabledCheckValue !== undefined && disabledValues?.includes(option.disabledCheckValue!)) ?? false

          return (
            <Option
              selected={value === option.value}
              disabled={isDisabled}
              onClick={isDisabled ? (e) => e?.stopPropagation() : () => onClickOption(option.value as T)}
              key={`select-box-${option?.key || idx}-${option.value}`}
              kind={value === option.value ? 'Body_14_bold' : 'Body_14_regular'}
            >
              {`${option.label}`}
            </Option>
          )
        })
      )}
    </SelectBoxOptions>
  )

  const isAllLabel = useMemo(() => value === allValue && hasAll, [allValue, hasAll])
  const isPlaceholder = useMemo(() => !allLabel && !isEmpty(placeholder), [allLabel, placeholder])
  const isSelected = useMemo(() => {
    if (allValue === null && value === null) return true // allValue이 null인 경우 별도 처리
    return !isNil(value) && !isNil(optionMap[`${value}`])
  }, [optionMap, value])

  const color = useMemo(() => {
    if (disabled) return colorSystem.text.common.disabled
    if (readonly) return colorSystem.text.common.primary
    if (isSelected) return colorSystem.text.common.primary
    if (isAllLabel) return colorSystem.text.common.primary
    if (isPlaceholder) return colorSystem.text.common.tertiary
    return colorSystem.text.common.tertiary
  }, [disabled, readonly, isSelected, isAllLabel, isPlaceholder])

  return (
    <Wrapper className={className} ref={$ref} $minWidth={minWidth} style={style}>
      {label && <FieldLabel required={required} content={label} />}
      <SelectBoxWrap
        $label={!!label}
        disabled={disabled || readonly}
        align="center"
        justify="space-between"
        ref={$select}
        onClick={open ? handleClose : handleOpen}
        $isOpen={open}
        $size={size}
        kind={kind}
      >
        <Value kind={kind === 'highlight' ? 'Body_14_bold' : 'Body_14_regular'} color={color} $size={size}>
          {isSelected ? `${optionMap[`${value}`]?.label}` : placeholder}
        </Value>
        <HStack align="center">
          {onClear && isSelected && <ClearIcon name="ic-closeCircle" filled size={16} colorToken="button-tertiary-fg" onClick={onClear} />}
          {!readonly && (
            <Icon
              name={`arrow-${open ? 'up' : 'down'}-filled`}
              size={16}
              color={kind === 'highlight' ? (disabled ? 'gray400' : 'gray800') : disabled ? 'gray300' : 'gray500'}
            />
          )}
        </HStack>
      </SelectBoxWrap>
      {open && !disabled && !readonly ? (portalId ? createPortal(optionDom, document.getElementById(portalId) ?? document.body) : optionDom) : null}
    </Wrapper>
  )
}

const ClearIcon = styled(IconBase)`
  cursor: pointer;
`

const Wrapper = styled.div<{ $minWidth?: number }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: ${({ $minWidth }) => $minWidth || 120}px;
  position: relative;
`

const SelectBoxWrap = styled(HStack)<{ $label?: boolean; disabled?: boolean; $isOpen?: boolean; kind?: 'normal' | 'highlight'; $size: Size }>`
  position: relative;
  width: ${({ $label }) => ($label ? 'calc(100% - 104px)' : '100%')};
  background-color: ${({ disabled }) => (disabled ? colorSystem.bg.input.disabled : colors.white)};
  padding: 2px 8px;
  height: ${({ $size }) => SIZE[$size]}px;
  border: 1px solid;
  border-color: ${({ $isOpen }) => ($isOpen ? colorSystem.border.element_active : colorSystem.border.element)};
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  color: ${colorSystem.text.common.primary};
  ${fontSystem.Body_14_regular};

  ${({ kind, disabled }) =>
    kind === 'highlight' &&
    css`
      border: 0;
      border-radius: 6px;
      background-color: ${disabled ? colorSystem.bg.common.primary_disabled : colors.blue100};

      &:active,
      &:hover {
        background-color: ${disabled ? colorSystem.bg.common.primary_disabled : colorSystem.bg.common.primary_hover_inverse};
      }
    `}
  align-items: center;

  ${ClearIcon} {
    display: none;
  }

  &:hover {
    ${ClearIcon} {
      display: block;
    }
  }
`

const Value = styled(Text)<{ $size: Size }>`
  display: block;
  overflow-x: auto;
  transform: translateY(1px);
`

const SelectBoxOptions = styled(VStack)<{ $block?: boolean; $label?: boolean; $position?: 'top' | 'bottom'; $size?: Size; $maxHeight?: number }>`
  width: ${({ $label, $block }) => ($label || $block ? '100%' : 'calc(100% - 104px)')};
  position: absolute;

  ${({ $position, $size }) =>
    $position === 'top'
      ? css`
          bottom: ${SIZE[$size || 'md'] + 4}px;
        `
      : css`
          top: ${SIZE[$size || 'md'] + 4}px;
        `};

  right: 0;
  z-index: ${zIndexSystem.modal + 1};
  max-height: ${({ $maxHeight }) => $maxHeight || 233}px;
  overflow-y: scroll;

  border-radius: 4px;
  border: 1px solid ${colorSystem.border.element};
  background: ${colors.white};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
`

const Option = styled(Text)<{ selected?: boolean; disabled?: boolean }>`
  width: 100%;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  text-align: left;

  background-color: ${({ disabled }) => (disabled ? colorSystem.bg.input.disabled : colors.white)};

  &:hover {
    background: ${({ disabled }) => !disabled && colorSystem.bg.common.secondary_hover};
  }
`

const SelectBox = React.forwardRef(SelectBoxInner) as <T>(
  props: SelectBoxProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof SelectBoxInner>

export const SelectBoxStyledComponents = {
  Wrapper,
  SelectBoxWrap,
  Option,
  SelectBoxOptions,
}

export default SelectBox
